import { useEffect, useRef } from "react";

import { PlayerType } from '../../model';
import { usePlayerAllActions } from './use-player-all-actions';
import { usePlayerStore } from './use-player-store';


export const usePlayersPause = () => {
  const { pause: pauseAudio } = usePlayerAllActions(PlayerType.Audio);
  const { pause: pauseVideo } = usePlayerAllActions(PlayerType.Video);

  const videoPlaying = usePlayerStore(PlayerType.Video, (s) => s.playing);
  const audioPlaying = usePlayerStore(PlayerType.Audio, (s) => s.playing);

  const videoActiveId = usePlayerStore(PlayerType.Video, (s) => s.id);
  const audioActiveId = usePlayerStore(PlayerType.Audio, (s) => s.id);

  const videoPending = usePlayerStore(PlayerType.Video, (s) => s.pendingPlayByUser);
  const audioPending = usePlayerStore(PlayerType.Audio, (s) => s.pendingPlayByUser);

  const desiredRef = useRef<PlayerType | null>(null);
  const prevAudioPending = useRef(false);
  const prevVideoPending = useRef(false);

  const lastVideoActiveIdRef = useRef<string | null>(null);
  const lastAudioActiveIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (videoActiveId) lastVideoActiveIdRef.current = videoActiveId;
    if (audioActiveId) lastAudioActiveIdRef.current = audioActiveId;
  }, [ videoActiveId, audioActiveId ]);

  useEffect(() => {
    const videoStore = window.videoPlayerStore;
    const audioStore = window.audioPlayerStore;
    if (!videoStore || !audioStore) return;

    const audioRise = audioPending && !prevAudioPending.current;
    const videoRise = videoPending && !prevVideoPending.current;

    if (audioRise) {
      desiredRef.current = PlayerType.Audio;

      if (videoPlaying) {
        pauseVideo();
        audioStore.set?.({
          pendingPlayByUser: true,
        });
      } else if (!audioPlaying) {
        audioStore.playerActions.play?.();
        desiredRef.current = null;
      }
    }

    if (videoRise) {
      desiredRef.current = PlayerType.Video;

      if (audioPlaying) {
        pauseAudio();
        videoStore.set?.({
          pendingPlayByUser: true,
        });
      } else if (!videoPlaying) {
        videoStore.playerActions.play?.();
        desiredRef.current = null;
      }
    }

    if (videoPlaying && audioPlaying) {
      pauseAudio();
    }

    prevAudioPending.current = audioPending;
    prevVideoPending.current = videoPending;
  }, [
    audioPending,
    videoPending,
    videoPlaying,
    audioPlaying,
    pauseAudio,
    pauseVideo,
  ]);

  useEffect(() => {
    const videoStore = window.videoPlayerStore;
    const audioStore = window.audioPlayerStore;
    if (!videoStore || !audioStore) return;

    if (desiredRef.current === PlayerType.Audio && !videoPlaying) {
      audioStore.playerActions.play?.();
      desiredRef.current = null;
    }

    if (desiredRef.current === PlayerType.Video && !audioPlaying) {
      videoStore.playerActions.play?.();
      desiredRef.current = null;
    }
  }, [ videoPlaying, audioPlaying ]);

  useEffect(() => {
    const audioStore = window.audioPlayerStore;
    if (!audioStore) return;
    if (!videoPlaying) return;
    if (desiredRef.current === PlayerType.Audio) return;

    const currentAudioId = audioStore.get().id;
    const audioIdChanged = currentAudioId !== lastAudioActiveIdRef.current;

    if (audioStore.get().playing && audioIdChanged) {
      audioStore.playerActions.pause?.();
    }
  }, [ videoPlaying, audioActiveId ]);
};
